/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/insertQaTemplateSelection.ts
 * FILE_ID: QA-TEMPLATES-INSERT-RUN-001
 * PURPOSE: Insert a library template or the blank checklist into the open QA.
 */
import type { QaApi } from '../../../qa/qaApi'
import { nextDefaultQaNames } from '../../../qa/qaApiHelpers'
import type { QaTemplateInsertResult } from '../../../qa/qaTemplateTypes'
import { runTemplateInserts, TemplateInsertError, type QaTemplateBasketRow } from './qaTemplateInsert'
import { resolveTemplateInsertTarget, storedWriteChoice, type QaTemplateWriteChoice } from './qaTemplateWriteTarget'
import type { QaTemplateProductDraft } from './qaTemplateProductDraft'
import type { QaTemplatePickerWorkspace } from './useQaTemplatePicker'

export async function insertQaTemplateSelection(input: {
  api: QaApi
  workspace: QaTemplatePickerWorkspace
  blankActive: boolean
  busy: boolean
  pendingNames: string[]
  targetAreaIds: Set<string>
  basketCounts: { checklists: number }
  basketEntries: QaTemplateBasketRow[]
  productDraft: QaTemplateProductDraft
  intoChecklist?: { checklistId: string; areaId: string } | null
  writeChoice: { current: QaTemplateWriteChoice | null }
  newQaName: { current: string }
  setBusy: (value: boolean) => void
  setError: (value: string | null) => void
  setResult: (value: QaTemplateInsertResult) => void
  setStep: (value: 'inserted') => void
  setAskWhere: (value: boolean) => void
  answerWriteTarget: (choice: QaTemplateWriteChoice) => void
}) {
  const {
    api, workspace, blankActive, busy, pendingNames, targetAreaIds,
    basketCounts, basketEntries, productDraft, intoChecklist, writeChoice, newQaName,
    setBusy, setError, setResult, setStep, setAskWhere, answerWriteTarget,
  } = input
  if (blankActive) {
    if (busy || (pendingNames.length === 0 && targetAreaIds.size === 0)) return
    setBusy(true)
    setError(null)
    try {
      const created = await api.insertBlank({
        job_id: workspace.jobId ?? 0,
        inspection_id: workspace.activeInspectionId,
        area_names: pendingNames.length ? pendingNames : ['Area'],
        area_ids: [...targetAreaIds],
      })
      workspace.invalidateAreas?.()
      workspace.invalidateExecution()
      workspace.setAreaMode('build')
      const areaId = created.areas[0]?.id ?? null
      if (areaId) workspace.selectArea(areaId)
      const checklistId = created.checklists[0]?.id
      if (checklistId) workspace.setActiveChecklistId(checklistId)
      setResult({
        created: created.checklists.map((row) => ({
          id: row.id,
          area: areaId ?? undefined,
          template_name: row.template_name || 'checklist',
        })),
        counts: {
          areas: created.areas.length,
          checklists: created.checklists.length,
          items: created.checklists.length,
        },
      })
      setStep('inserted')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not insert the template.')
    } finally {
      setBusy(false)
    }
    return
  }
  if (basketCounts.checklists === 0 || busy || basketEntries.length === 0) return
  const inspections = workspace.inspections ?? []
  const pending = storedWriteChoice(workspace.pendingWriteChoice, writeChoice.current != null)
  if (pending) { workspace.consumePendingWriteChoice?.(); answerWriteTarget(pending); return }
  const choice = writeChoice.current ?? 'add'
  const typedName = newQaName.current
  writeChoice.current = null
  newQaName.current = ''
  setAskWhere(false)
  if (choice !== 'create' && !workspace.activeInspectionId) return
  setBusy(true)
  setError(null)
  const total = basketEntries.length
  let done = 0
  const clearedName = inspections.find((row) => row.id === workspace.activeInspectionId)?.inspection_name || 'this QA'
  let cleared = false
  try {
    // Reused from: templates/qaTemplateWriteTarget.ts (resolveTemplateInsertTarget)
    const target = await resolveTemplateInsertTarget({
      api,
      jobId: workspace.jobId ?? 0,
      workspace,
      choice,
      name: typedName || nextDefaultQaNames(inspections.map((row) => row.inspection_name), 1, workspace.jobId)[0],
      targetAreaIds: [...targetAreaIds],
    })
    cleared = choice === 'replace'
    const { merged, done: insertedCount } = await runTemplateInserts({
      api,
      rows: basketEntries,
      targetAreaIds: intoChecklist ? [intoChecklist.areaId] : target.targetAreaIds,
      inspectionId: target.inspectionId,
      productDraft,
      intoChecklistId: intoChecklist?.checklistId,
    })
    done = insertedCount
    setResult(merged)
    workspace.invalidateExecution()
    workspace.invalidateAreas?.()
    workspace.setAreaMode('build')
    const locationId = merged.location_area_id || merged.placements?.[0]?.area_id || null
    if (locationId) workspace.selectArea(locationId)
    const created = merged.created.find((row) => locationId && row.area === locationId) || merged.created[0]
    if (intoChecklist) workspace.setActiveChecklistId(intoChecklist.checklistId)
    else if (created?.id) workspace.setActiveChecklistId(created.id)
    setStep('inserted')
  } catch (err) {
    const partial = err instanceof TemplateInsertError ? err : null
    const message = err instanceof Error ? err.message : 'Could not insert the template.'
    const finished = partial?.done ?? done
    if (cleared) {
      setError(`${clearedName} was cleared, but the templates did not insert: ${message}. Press Insert again.`)
      workspace.invalidateExecution()
      workspace.invalidateAreas?.()
    } else if (finished > 0) {
      if (partial) setResult(partial.merged)
      setError(`Inserted ${finished} of ${total} templates. ${message}`)
      workspace.invalidateExecution()
      workspace.invalidateAreas?.()
    } else {
      setError(message)
    }
  } finally {
    setBusy(false)
  }
}
